export interface FacetSettings {
  order: string[];
  hidden: string[];
}

const COOKIE_PREFIX = "lootstack-facets-";

function cookieKey(context: string): string {
  return `${COOKIE_PREFIX}${context}`;
}

function getDefaultSettings(defaultOrder: readonly string[]): FacetSettings {
  return { order: [...defaultOrder], hidden: [] };
}

/**
 * Reconciles a saved settings object against the current registry.
 * - Keys in cookie but not in registry are removed (stale facet).
 * - Keys in registry but not in cookie order are appended at the end (new facet).
 */
function reconcile(
  saved: FacetSettings,
  defaultOrder: readonly string[]
): FacetSettings {
  const defaultKeys = new Set(defaultOrder);
  const validOrder = saved.order.filter((k) => defaultKeys.has(k));
  const present = new Set(validOrder);
  for (const key of defaultOrder) {
    if (!present.has(key)) {
      validOrder.push(key);
    }
  }
  const validHidden = saved.hidden.filter((k) => defaultKeys.has(k));
  return { order: validOrder, hidden: validHidden };
}

/**
 * Reads facet settings from a client-side cookie.
 * Returns reconciled defaults if no cookie or parse error.
 */
export function getFacetSettingsFromClientCookie(
  context: string,
  defaultOrder: readonly string[]
): FacetSettings {
  const key = cookieKey(context);
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${key}=([^;]+)`)
  );
  if (!match) return getDefaultSettings(defaultOrder);

  try {
    const decoded = decodeURIComponent(match[1]);
    const parsed = JSON.parse(decoded) as FacetSettings;
    if (!Array.isArray(parsed.order) || !Array.isArray(parsed.hidden)) {
      return getDefaultSettings(defaultOrder);
    }
    return reconcile(parsed, defaultOrder);
  } catch {
    return getDefaultSettings(defaultOrder);
  }
}

/**
 * Writes facet settings to a client-side cookie.
 * 1-year expiry, Lax SameSite.
 */
export function setFacetSettingsCookie(context: string, settings: FacetSettings): void {
  const key = cookieKey(context);
  const value = encodeURIComponent(JSON.stringify(settings));
  document.cookie = `${key}=${value}; path=/; max-age=31536000; SameSite=Lax`;
}
